import { z } from 'zod';

/**
 * Partial-update (PATCH) semantics, defined once for the whole platform.
 *
 * ---------------------------------------------------------------------------
 * THE RULE
 * ---------------------------------------------------------------------------
 *
 * A PATCH body is not "the record, with holes". It is a set of instructions,
 * and the KEY decides which instruction each field carries:
 *
 *   - the key is ABSENT (`undefined`) - untouched, carry the stored value
 *     forward;
 *   - the key is present and `null`   - CLEARED, because an emptied box on a
 *     form has no other way to say so;
 *   - the key is present with a value - set it to that value.
 *
 * There is no fourth case, and no route may invent one. Folding `null` into
 * "absent" is what made the emptied box a lie: an owner who deleted a stale
 * access code, intercom name or contact number was answered 200 and handed the
 * old value back on the next read, with the courier still being briefed with
 * it. Folding "absent" into "clear" is the mirror bug: a PATCH carrying only
 * `{ status: 'PAUSED' }` blanks every field the caller never mentioned.
 *
 * This module exists because that rule had been reimplemented independently at
 * eight-plus call sites in four mutually incompatible styles, and two of those
 * implementations silently dropped fields the contract accepts. The rule is
 * cross-cutting; a cross-cutting rule that lives in each caller is a rule that
 * is wrong somewhere. Everything below is one rule expressed three times, for
 * the three places it has to hold:
 *
 *   `patchSchema()`  - on the wire:   which shapes may say "untouched"
 *   `applyPatch()`   - on the server: how "untouched" merges onto the record
 *   `formPatch()`    - in the form:   how an emptied box becomes "cleared"
 *
 * ---------------------------------------------------------------------------
 * THE ZOD 4 TRAP THIS BUILDER EXISTS TO CLOSE
 * ---------------------------------------------------------------------------
 *
 * zod 4 - unlike zod 3 - keeps a `.default()` THROUGH `.partial()`:
 *
 *   z.object({ a: z.boolean().default(false) }).partial().parse({})
 *   // => { a: false }   NOT {}
 *
 * `.partial()` wraps each key as `optional(default(inner))`, and an optional
 * whose inner type carries a default still materialises that default when the
 * key is missing. So a PATCH body derived from a creation schema arrives with
 * every defaulted field present on EVERY request, whatever the caller sent, and
 * a route asking "did the caller touch this?" with `body.x !== undefined` gets
 * `true` every time. Nothing is optional about it; the shape simply lies.
 *
 * That is not a style point. It shipped: `PropertyInput` defaults
 * `accessCodeMasked` to `false`, `countryCode` to `'FR'` and `timeZone` to
 * `'Europe/Paris'`, so pausing an apartment - the most ordinary action on the
 * Apartments tab, a PATCH carrying nothing but `{ status: 'PAUSED' }` -
 * silently withdrew the safe-drop permission the owner had given the courier
 * (PRD GST-007, WEB-003) and rewrote the country and the timezone alongside it.
 *
 * `patchSchema()` strips the wrapper rather than trusting anyone to remember
 * this, and `findSurvivingDefaults()` plus the package-wide test in
 * `patch.test.ts` make a reintroduction anywhere in `@croissants/contracts`
 * fail the suite rather than fail a guest.
 */

// ---------------------------------------------------------------------------
// Zod node access
//
// zod 4 keeps the schema graph on `._zod.def`, typed only as far as `type`.
// These two readers are the single place that reaches into it, so a zod upgrade
// has one thing to fix rather than a walker's worth.
// ---------------------------------------------------------------------------

type ZodNode = z.core.$ZodType;

interface AnyDef {
  readonly type: string;
  readonly innerType?: ZodNode;
  readonly shape?: Readonly<Record<string, ZodNode>>;
  readonly catchall?: ZodNode;
  readonly element?: ZodNode;
  readonly items?: readonly ZodNode[];
  readonly rest?: ZodNode;
  readonly options?: readonly ZodNode[];
  readonly left?: ZodNode;
  readonly right?: ZodNode;
  readonly keyType?: ZodNode;
  readonly valueType?: ZodNode;
  readonly in?: ZodNode;
  readonly out?: ZodNode;
  readonly getter?: () => ZodNode;
}

function defOf(node: unknown): AnyDef | null {
  const internals = (node as { _zod?: { def?: AnyDef } } | null | undefined)?._zod;
  return internals?.def ?? null;
}

/** Wrappers that pass a value through to an inner schema without reshaping it. */
const PASSTHROUGH_WRAPPERS = new Set([
  'optional',
  'nullable',
  'readonly',
  'nonoptional',
  'catch',
  'promise',
]);

/** The two wrappers that can materialise a value the caller never sent. */
const DEFAULTING_WRAPPERS = new Set(['default', 'prefault']);

// ---------------------------------------------------------------------------
// 1. patchSchema - the wire shape
// ---------------------------------------------------------------------------

type Unwrapped<T> =
  T extends z.ZodDefault<infer Inner>
    ? Unwrapped<Inner>
    : T extends z.ZodPrefault<infer Inner>
      ? Unwrapped<Inner>
      : T;

/** Every key optional, every platform default stripped off it. */
export type PatchShape<Shape extends z.ZodRawShape> = {
  [K in keyof Shape]: z.ZodOptional<Unwrapped<Shape[K]>>;
};

/**
 * Build the PATCH body for a set of editable fields.
 *
 * Every key becomes optional and every `.default()` / `.prefault()` is removed,
 * so `parse({})` answers `{}` and `body.x !== undefined` means what it says:
 * the caller mentioned `x`. A nullable field keeps its nullability, which is
 * what gives the wire three distinguishable states rather than two -
 * omitted / explicit-null / value.
 *
 * Defaults belong to creation alone. Feed this the UNDEFAULTED field shape and
 * apply `.default()` on the creation schema built from the same shape:
 *
 *   const Fields  = z.object({ accessCodeMasked: z.boolean(), ... });
 *   const Input   = Fields.extend({ accessCodeMasked: Fields.shape.accessCodeMasked.default(false) });
 *   const Patch   = patchSchema(Fields);
 *
 * Passing a defaulted shape is not an error - the builder strips it - but the
 * creation defaults then have to live somewhere, so keep the shapes this way
 * round.
 *
 * Passing an ALREADY-PARTIAL shape is not an error either, and it deliberately
 * cannot be made one: `X.partial()` produces a shape whose values are
 * `ZodOptional`, and so does any schema with a legitimately optional field -
 * `CatalogueItemDraft` (`rank`, `listed`, `priceTracksComponents`, `formule`, `bundled` -
 * each `.optional()` and deliberately never `.default()`) and
 * `PartnerProductFields` (`ingredientsFr`) both hand this builder one. The two are
 * the same TYPE, so a constraint that refused the first would refuse the
 * second, and both of those bodies with it. `stripDefaults` recursing through
 * the `optional` is what makes the belt-and-braces call safe instead; the shape
 * below is still the one to write.
 *
 * Deliberately SHALLOW. An object-valued field is replaced wholesale, because a
 * merge helper and a schema that disagree about depth are how this class of bug
 * starts. Give a nested group its own patch schema and its own merge, which
 * stays one rule applied twice:
 *
 *   body:  patchSchema(Settings).extend({
 *            alerts: patchSchema(Settings.shape.alerts).optional(),
 *          })
 *   merge: applyPatch(current, { ...body, alerts: applyPatch(current.alerts, body.alerts) })
 *
 * (`applyPatch(x, undefined)` returns `x`, so the nested call needs no guard.)
 */
export function patchSchema<Shape extends z.ZodRawShape>(
  fields: z.ZodObject<Shape>,
): z.ZodObject<PatchShape<Shape>> {
  const shape = fields.shape as unknown as Readonly<Record<string, z.ZodType>>;
  const patched: Record<string, z.ZodType> = {};
  for (const key of Object.keys(shape)) {
    const field = shape[key];
    if (field === undefined) continue;
    patched[key] = z.optional(stripDefaults(field));
  }
  return z.object(patched) as unknown as z.ZodObject<PatchShape<Shape>>;
}

/**
 * The wrappers a default can hide UNDER and that this module can put back
 * exactly as it found them.
 *
 * `optional` and `nullable` are the two `.partial()` and `.nullable()` produce,
 * and they are the two `findSurvivingDefaults` has always reported a default
 * beneath, so they are the two worth peeling through. The other four
 * passthrough wrappers are deliberately left alone, each for its own reason:
 * `catch` carries a fallback value and `promise` a whole different call shape,
 * `readonly` and `nonoptional` each restate a fact about the key that a rebuild
 * would have to re-derive from the shape around it - and rebuilding any of them
 * from its `_zod.def` would be this module quietly reconstructing a schema it
 * does not fully understand. A default hidden under one of THOSE is still
 * caught by the package-wide walk in `patch.test.ts`, loudly, with a path.
 *
 * A `Map` rather than an object literal because the key comes off `_zod.def` -
 * a string this module did not choose. An object literal answers
 * `Object.prototype`'s own members for `constructor` and `toString`, so a node
 * typed either of those would have handed `stripDefaults` a "rebuilder" that is
 * not one. No zod node is named that today; a lookup table indexed by foreign
 * strings should not depend on that.
 */
type Rebuild = (inner: z.ZodType) => z.ZodType;

const REBUILDABLE_WRAPPERS: ReadonlyMap<string, Rebuild> = new Map<string, Rebuild>([
  ['optional', (inner) => z.optional(inner)],
  ['nullable', (inner) => z.nullable(inner)],
]);

/**
 * Peel `.default()` / `.prefault()` off a field schema, including the ones an
 * `optional` or a `nullable` is sitting on top of.
 *
 * The outside-in loop is the obvious half: `z.boolean().default(false)` is
 * `default(boolean)` and one pass answers `boolean`. The recursion is the half
 * that was missing, and what it cost was exactly the trap this module exists to
 * close, one level further down.
 *
 * `patchSchema(Fields.partial())` reads like belt and braces - the caller has
 * asked for optional keys twice - and `.partial()` has already produced
 * `optional(default(x))`. A loop that returns at the first non-defaulting
 * wrapper met that `optional`, stopped, and handed the default back intact: the
 * builder was given a defaulted shape, reported success, and materialised
 * `{flag: false}` out of `parse({})` anyway. Two internal PATCH bodies were
 * written that way (catalogue items and product kinds). Neither leaked, because
 * neither shape carried a default - but the only thing standing between that
 * shape and a live defect was somebody not adding one, which is not a guard.
 *
 * So the promise two screens up - "Passing a defaulted shape is not an error -
 * the builder strips it" - now holds wherever the default sits. Recursing
 * rather than flattening is what keeps the wrappers themselves: an optional
 * must stay optional and a nullable must stay nullable, because those are what
 * give the wire its three distinguishable states. Only the defaulting wrapper
 * is removed, and the ORIGINAL node is returned when there was nothing to
 * remove, so a schema with no defaults is never rebuilt.
 */
function stripDefaults(field: z.ZodType): z.ZodType {
  let current: z.ZodType = field;
  for (;;) {
    const def = defOf(current);
    if (def === null || def.innerType === undefined) return current;
    if (DEFAULTING_WRAPPERS.has(def.type)) {
      current = def.innerType as z.ZodType;
      continue;
    }
    const rebuild = REBUILDABLE_WRAPPERS.get(def.type);
    if (rebuild === undefined) return current;
    const inner = def.innerType as z.ZodType;
    const stripped = stripDefaults(inner);
    return stripped === inner ? current : rebuild(stripped);
  }
}

// ---------------------------------------------------------------------------
// 2. applyPatch - the server-side merge
// ---------------------------------------------------------------------------

/**
 * The patch view of a record: every field optional, `undefined` meaning
 * untouched. A field typed `T | null` on the record is therefore
 * `T | null | undefined` here - the three states, spelled out by the type.
 */
export type Patch<T> = { [K in keyof T]?: T[K] | undefined };

/**
 * Merge a parsed PATCH body onto the record it edits, applying THE RULE at the
 * top of this file and nothing else: omitted = unchanged, `null` = clear,
 * value = set.
 *
 * Written as one loop over the keys the caller actually sent, because every
 * hand-rolled variant of this drifted. The conditional-spread style
 * (`...(body.x !== undefined ? { x: body.x } : {})`) has to be repeated per
 * field and a forgotten field is invisible - that is how two call sites came to
 * accept a field on the wire and drop it on the floor. The in-place style
 * (`if (body.x !== undefined) row.x = body.x`) mutates the record before
 * validation has finished, so a 422 raised later leaves the earlier fields
 * permanently changed.
 *
 * Neither is possible here: nothing is enumerated by hand, and `current` is
 * never mutated. The result is a new object, or `current` itself when the patch
 * carried no instruction at all.
 *
 * `patch` may be `null` / `undefined` (nothing to apply) so a nested group
 * merges without a guard - see the example on `patchSchema`.
 *
 * It is a MERGE, not a sanitiser: pass it the output of a patch schema's
 * `parse`, which has already stripped unknown keys and rejected bad values.
 */
export function applyPatch<T extends object>(current: T, patch: Patch<T> | null | undefined): T {
  if (patch === null || patch === undefined) return current;
  const sent = patch as Readonly<Record<string, unknown>>;
  let next: Record<string, unknown> | null = null;
  for (const key of Object.keys(sent)) {
    const value = sent[key];
    // Omitted: no instruction. `Object.keys` still lists a key written as an
    // explicit `undefined`, which means the same thing and is skipped here too.
    if (value === undefined) continue;
    next ??= { ...(current as unknown as Record<string, unknown>) };
    // `null` falls through as `null` - that is the clear.
    next[key] = value;
  }
  return next === null ? current : (next as T);
}

/**
 * THE RULE for one field, when the target stores absence as `undefined` rather
 * than `null` - a domain entity with optional properties, where `applyPatch`
 * cannot be used because the record's key names differ from the wire's.
 *
 *   `sent === undefined` - untouched, keep `previous`
 *   `sent === null`      - cleared, answer `undefined`
 *   a value              - that value
 *
 * The `null`-to-`undefined` step is the whole point: `null` on the wire and
 * `undefined` in the entity are the same fact - this field has no value -
 * expressed in the two vocabularies. Reading `sent ?? previous` instead is the
 * bug this replaces, because it treats an explicit clear as silence.
 */
export function patchedField<T>(
  sent: T | null | undefined,
  previous: T | undefined,
): T | undefined {
  if (sent === undefined) return previous;
  return sent ?? undefined;
}

/**
 * Writes a `patchedField` result onto an OPTIONAL property, and the reason it
 * has to exist.
 *
 * `patchedField` answers `undefined` for a clear, and `applyPatch` SKIPS an
 * `undefined` because that is how it recognises an omitted field - so
 * `applyPatch(entity, { logoRef: patchedField(body.logoUrl, ...) })` reads a
 * deliberate clear as silence and keeps the old value. Both readings of
 * `undefined` are correct in their own place; composing them is what is wrong,
 * and it was wrong at both call sites that tried it: withdrawing a mode B
 * shop's logo and clearing a shop address's second line were each accepted with
 * a 200 that changed nothing.
 *
 *   value !== undefined  - set it
 *   value === undefined  - REMOVE the key, which is how an entity spells "no
 *                          value" for an optional property
 *
 * Never applied to a required property: removing one leaves an entity the
 * domain cannot describe.
 */
export function clearableField<T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined,
): T {
  if (value !== undefined) return { ...target, [key]: value };
  const next: Record<string, unknown> = { ...(target as unknown as Record<string, unknown>) };
  delete next[key as string];
  return next as T;
}

/**
 * Does this patch say anything about any of these fields?
 *
 * For the "only cut a new version / only re-run the expensive step if the
 * caller touched this group" decision, which was previously a hand-written
 * chain of `body.a !== undefined || body.b !== undefined || ...` that had to be
 * extended by hand every time a field joined the group.
 */
export function patchTouches<T extends object>(
  patch: Patch<T> | null | undefined,
  keys: readonly (keyof T)[],
): boolean {
  if (patch === null || patch === undefined) return false;
  const sent = patch as Readonly<Record<string, unknown>>;
  return keys.some((key) => sent[key as string] !== undefined);
}

// ---------------------------------------------------------------------------
// 3. formPatch - the browser-side normalization
// ---------------------------------------------------------------------------

/**
 * An `<input>` has no `null`. Whatever the user cleared comes back as `''`, and
 * `''` is not what a nullable field means by empty - it is a zero-length string,
 * which a `min(1)` rejects and a nullable column happily stores as "not null,
 * just blank". Every form therefore needs the same conversion, and every form
 * had written its own `blankToNull` (twice verbatim, once as `'' means absent`,
 * which silently made an emptied box unclearable).
 */
const ACCEPTS_NULL = new WeakMap<object, boolean>();

/**
 * Can this field be explicitly cleared? Asked of the schema rather than of a
 * hand-kept list of field names, so adding a nullable field to a contract is
 * all it takes for the form to clear it correctly.
 */
function acceptsNull(field: z.ZodType): boolean {
  const cached = ACCEPTS_NULL.get(field as unknown as object);
  if (cached !== undefined) return cached;
  const answer = field.safeParse(null).success;
  ACCEPTS_NULL.set(field as unknown as object, answer);
  return answer;
}

/**
 * Turn raw form state into a patch body, applying THE RULE from the form's side.
 *
 *   - a key absent from `values`, or `undefined` - stays absent (untouched);
 *   - a string          - trimmed; an empty one becomes `null` when the field
 *                         is nullable, and stays `''` when it is not, so the
 *                         schema's own `min(1)` reports the emptied REQUIRED
 *                         box instead of the form quietly dropping it;
 *   - `null`            - passes through (an explicit clear);
 *   - anything else     - passed through untouched;
 *   - a key the schema does not declare - dropped.
 *
 * It normalises BLANKNESS, not types: a `<select>` holding `'yes'` for a
 * `boolean | null` field is still the form's job to map. Only the empty case is
 * cross-cutting, and only the empty case is handled here.
 */
export function normalizeFormValues<Shape extends z.ZodRawShape>(
  schema: z.ZodObject<Shape>,
  values: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const shape = schema.shape as unknown as Readonly<Record<string, z.ZodType>>;
  const normalized: Record<string, unknown> = {};
  for (const key of Object.keys(shape)) {
    if (!(key in values)) continue;
    const raw = values[key];
    if (raw === undefined) continue;
    const field = shape[key];
    if (typeof raw === 'string' && field !== undefined) {
      const trimmed = raw.trim();
      normalized[key] = trimmed === '' && acceptsNull(field) ? null : trimmed;
      continue;
    }
    normalized[key] = raw;
  }
  return normalized;
}

/**
 * `normalizeFormValues` then `parse`. Throws `z.ZodError` on an invalid form,
 * which is what a submit handler wants when the fields were already validated
 * on the way in; use `safeFormPatch` to render per-field messages instead.
 */
export function formPatch<Shape extends z.ZodRawShape>(
  schema: z.ZodObject<Shape>,
  values: Readonly<Record<string, unknown>>,
): z.output<z.ZodObject<Shape>> {
  return schema.parse(normalizeFormValues(schema, values));
}

/** `formPatch` without the throw: the zod result, for forms that show errors. */
export function safeFormPatch<Shape extends z.ZodRawShape>(
  schema: z.ZodObject<Shape>,
  values: Readonly<Record<string, unknown>>,
): ReturnType<z.ZodObject<Shape>['safeParse']> {
  return schema.safeParse(normalizeFormValues(schema, values));
}

// ---------------------------------------------------------------------------
// 4. findSurvivingDefaults - the guard that keeps the trap closed
// ---------------------------------------------------------------------------

export interface SurvivingDefault {
  /** Dotted path to the offending key, e.g. `body.accessCodeMasked`. */
  readonly path: string;
  readonly wrapper: 'default' | 'prefault';
}

/**
 * Find every key that is optional and yet still materialises a value when it is
 * omitted - the `optional(default(...))` shape `.partial()` leaves behind.
 *
 * This is the fingerprint of the trap described at the top of this file, and it
 * is a lie in every context, not only in a PATCH: if a key may be omitted, a
 * default underneath it either never fires (dead) or fires and denies the
 * caller the ability to omit anything (a lie). A `default` that is NOT under an
 * optional - `PageRequest.limit`, a query filter's `includeAcknowledged` - is
 * the legitimate case and is not reported.
 *
 * Walks wrapper chains only when deciding, so a default living inside a nested
 * object under an optional group is not reported: sending `{ alerts: {} }` is a
 * deliberate act, unlike omitting a key entirely.
 */
export function findSurvivingDefaults(schema: unknown, rootPath = ''): SurvivingDefault[] {
  const found: SurvivingDefault[] = [];
  const seen = new Set<unknown>();

  const walk = (node: unknown, path: string): void => {
    const def = defOf(node);
    if (def === null || seen.has(node)) return;
    seen.add(node);

    if (def.type === 'optional') {
      const wrapper = defaultBeneath(def.innerType);
      if (wrapper !== null) found.push({ path, wrapper });
    }

    if (def.shape !== undefined) {
      for (const key of Object.keys(def.shape)) {
        walk(def.shape[key], path === '' ? key : `${path}.${key}`);
      }
    }
    if (def.catchall !== undefined) walk(def.catchall, `${path}.*`);
    if (def.innerType !== undefined) walk(def.innerType, path);
    if (def.element !== undefined) walk(def.element, `${path}[]`);
    if (def.items !== undefined) {
      for (const [index, item] of def.items.entries()) walk(item, `${path}[${index}]`);
    }
    if (def.rest !== undefined) walk(def.rest, `${path}[]`);
    if (def.options !== undefined) {
      for (const [index, option] of def.options.entries()) walk(option, `${path}|${index}`);
    }
    if (def.left !== undefined) walk(def.left, path);
    if (def.right !== undefined) walk(def.right, path);
    if (def.keyType !== undefined) walk(def.keyType, `${path}[key]`);
    if (def.valueType !== undefined) walk(def.valueType, `${path}[value]`);
    if (def.in !== undefined) walk(def.in, path);
    if (def.out !== undefined) walk(def.out, path);
    if (def.getter !== undefined) {
      try {
        walk(def.getter(), path);
      } catch {
        // A lazy schema that cannot be resolved standing still is not this
        // guard's problem; the endpoint tests parse it for real.
      }
    }
  };

  walk(schema, rootPath);
  return found;
}

/**
 * From just inside an optional, is there a default before the shape changes?
 *
 * A `pipe` is followed on its INPUT side, because that is where the value the
 * caller never sent is materialised: `.transform()` and `.pipe()` both build a
 * pipe, so `z.string().default('x').transform(f)` under an optional is
 * `optional(pipe(default(string), transform))` - the default fires and the
 * transform then runs on it, exactly the lie this guard reports everywhere
 * else. Walking only the passthrough wrappers stopped at the pipe and said
 * nothing. No schema in this package is shaped that way today; the guard is
 * what keeps it that way, so it has to be able to see it.
 */
function defaultBeneath(inner: ZodNode | undefined): 'default' | 'prefault' | null {
  let current: unknown = inner;
  for (let depth = 0; depth < 32; depth += 1) {
    const def = defOf(current);
    if (def === null) return null;
    if (DEFAULTING_WRAPPERS.has(def.type)) return def.type as 'default' | 'prefault';
    if (def.type === 'pipe') {
      current = def.in;
      continue;
    }
    if (!PASSTHROUGH_WRAPPERS.has(def.type)) return null;
    current = def.innerType;
  }
  return null;
}
